import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PRODUCTS } from '../data/catalog';
import { ProductCard } from '../components/ProductCard';
import { inputCls } from '../components/ui';
import { useTitle } from '../components/Layout';

const CATEGORIES = ['wszystkie', ...new Set(PRODUCTS.map((p) => p.category))];
const SORTS = [
  { id: 'polecane', label: 'Polecane' },
  { id: 'cena_rosnaco', label: 'Cena: od najniższej' },
  { id: 'cena_malejaco', label: 'Cena: od najwyższej' },
  { id: 'ocena', label: 'Najwyżej oceniane' },
];

export function Catalog() {
  useTitle('Katalog');
  const [sp, setSp] = useSearchParams();

  const q = sp.get('szukaj') ?? '';
  const cat = sp.get('kategoria') ?? 'wszystkie';
  const sort = sp.get('sortuj') ?? 'polecane';

  const patch = (key: string, value: string) => {
    const next = new URLSearchParams(sp);
    if (!value || value === 'wszystkie' || value === 'polecane') next.delete(key);
    else next.set(key, value);
    setSp(next, { replace: true });
  };

  const results = useMemo(() => {
    let out = PRODUCTS.filter(
      (p) =>
        (cat === 'wszystkie' || p.category === cat) &&
        (q.trim() === '' ||
          `${p.name} ${p.brand} ${p.category} ${p.subcategory ?? ''}`.toLowerCase().includes(q.trim().toLowerCase())),
    );
    if (sort === 'cena_rosnaco') out = [...out].sort((a, b) => a.price - b.price);
    if (sort === 'cena_malejaco') out = [...out].sort((a, b) => b.price - a.price);
    if (sort === 'ocena') out = [...out].sort((a, b) => b.rating - a.rating);
    return out;
  }, [q, cat, sort]);

  return (
    <>
      <h1 className="mb-4 text-xl font-semibold text-white">
        {cat === 'wszystkie' ? 'Katalog' : cat}
        {q && <span className="ml-2 text-[14px] font-normal text-slate-400">— wyniki dla „{q}”</span>}
      </h1>

      <div className="mb-5 flex flex-wrap items-end gap-3">
        <div className="min-w-[220px] flex-1">
          <label className="mb-1 block text-xs text-slate-400" htmlFor="szukaj">
            Szukaj
          </label>
          <input
            id="szukaj"
            className={inputCls}
            value={q}
            onChange={(e) => patch('szukaj', e.target.value)}
            placeholder="fotel, lampa, Kolab…"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs text-slate-400" htmlFor="kategoria">
            Kategoria
          </label>
          <select id="kategoria" className={inputCls} value={cat} onChange={(e) => patch('kategoria', e.target.value)}>
            {CATEGORIES.map((c) => (
              <option key={c}>{c}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs text-slate-400" htmlFor="sortuj">
            Sortowanie
          </label>
          <select id="sortuj" className={inputCls} value={sort} onChange={(e) => patch('sortuj', e.target.value)}>
            {SORTS.map((s) => (
              <option key={s.id} value={s.id}>
                {s.label}
              </option>
            ))}
          </select>
        </div>
        <p className="pb-2 text-[12px] text-slate-500">{results.length} produktów</p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {results.map((p) => (
          <ProductCard key={p.id} product={p} />
        ))}
      </div>

      {results.length === 0 && (
        <p className="py-16 text-center text-sm text-slate-500">
          Nie znaleźliśmy niczego dla „{q}”. Spróbuj innej frazy.
        </p>
      )}
    </>
  );
}
